import React, { useEffect, useState, useMemo } from "react";
import * as echarts from "echarts";
import { useWebSocket } from "../ws/websocket";
import { getAutomationExecution } from "../service/automation-service";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "@mui/material";

const today = new Date();

// Helper function to convert MUI spacing to pixels
const spacingToPx = (value) => {
  if (typeof value === "number") {
    return `${value * 8}px`;
  }
  return value;
};

const AutomationHistoryStatistics = () => {
  const isSmallScreen = useMediaQuery("@media (max-width:599px)");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");
  const [filteredData, setFilteredData] = useState({});
  const { sendAndWaitWs } = useWebSocket();

  const startDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    0,
    0,
    0
  ).getTime();

  const endDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    23,
    59,
    59
  ).getTime();

  const fetchStatisticsData = async () => {
    // TODO: this method is removed in cloud, only valid if use local hub
    const resp = await getAutomationExecution(
      sendAndWaitWs,
      id,
      startDate,
      endDate
    );

    if (resp && !resp.error) {
      setFilteredData({
        hourAutomationHistoryStatistics: resp.records || [],
      });
    } else {
      setFilteredData({
        hourAutomationHistoryStatistics: [],
      });
    }
  };

  useEffect(() => {
    fetchStatisticsData();
  }, []);

  const hourData = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    successCount: 0,
    failCount: 0,
    runnedCount: 0,
  }));

  filteredData.hourAutomationHistoryStatistics?.forEach((item) => {
    const hourIndex = item.timeHour % 24;
    hourData[hourIndex].successCount += item.successCount;
    hourData[hourIndex].failCount += item.failCount;
    hourData[hourIndex].runnedCount += item.successCount + item.failCount;
  });

  const totalRunCount = filteredData.hourAutomationHistoryStatistics?.reduce(
    (sum, item) => sum + item.successCount + item.failCount,
    0
  );
  const totalSuccessCount =
    filteredData.hourAutomationHistoryStatistics?.reduce(
      (sum, item) => sum + item.successCount,
      0
    );
  const totalFailCount = filteredData.hourAutomationHistoryStatistics?.reduce(
    (sum, item) => sum + item.failCount,
    0
  );

  const successPercentage = totalRunCount
    ? ((totalSuccessCount / totalRunCount) * 100).toFixed(2)
    : 0;

  const failPercentage = totalRunCount
    ? ((totalFailCount / totalRunCount) * 100).toFixed(2)
    : 0;

  useEffect(() => {
    const chartDom = document.getElementById("history-chart");
    const myChart = echarts.init(chartDom);

    const option = {
      title: {
        text: "Today's Data Statistics",
        left: "center",

        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
      },
      legend: {
        data: ["Runned Count", "Success Count", "Failed Count"],
        top: "12%",
      },
      grid: {
        top: "30%",
        left: "10%",
        right: "10%",
        bottom: "10%",
      },
      xAxis: {
        type: "category",
        data: hourData.map((item) => item.hour),
        axisLabel: {
          interval: 3,
          formatter: (value) => `${value}h`,
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: filteredData
          ? Math.max(
              ...(filteredData.hourAutomationHistoryStatistics?.map(
                (item) => item.totalCount
              ) || [0])
            )
          : 100,
        axisLabel: {
          formatter: (value) => value.toLocaleString(),
        },

        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
      },
      series: [
        {
          name: "Total Count",
          type: "line",
          data: hourData?.map((item) => item.totalCount),
          animation: true,
        },
        {
          name: "Success Count",
          type: "line",
          data: hourData?.map((item) => item.successCount),
          animation: true,
        },
        {
          name: "Error Count",
          type: "line",
          data: hourData?.map((item) => item.failCount),
          animation: true,
        },
      ],
    };

    myChart.setOption(option);

    const handleResize = () => {
      myChart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      myChart.dispose();
      window.removeEventListener("resize", handleResize);
    };
  }, [filteredData]);

  useEffect(() => {
    const pieChartDom = document.getElementById("pie-chart");
    const pieChart = echarts.init(pieChartDom);

    const pieOption = {
      title: {
        text: "Success vs Failed Ratio",
        left: "center",
        top: "5%",
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "item",
        formatter: "{a} <br/>{b}: {c} ({d}%)",
      },
      series: [
        {
          name: "Ratio",
          type: "pie",
          radius: ["40%", "70%"],
          data: [
            {
              value: totalSuccessCount,
              name: "Success",
              itemStyle: { color: "#4caf50" },
            },
            {
              value: totalFailCount,
              name: "Failed",
              itemStyle: { color: "#ff9800" },
            },
          ],
          animation: true,
          label: {
            show: false,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
      graphic: [
        {
          type: "text",
          left: "center",
          top: "47%",
          style: {
            text: `Success: ${successPercentage}%`,
            textAlign: "center",
            fontSize: 14,
            fill: "#4caf50",
          },
        },
        {
          type: "text",
          left: "center",
          top: "54%",
          style: {
            text: `Failed: ${failPercentage}%`,
            textAlign: "center",
            fontSize: 14,
            fill: "#ff9800",
          },
        },
      ],
    };

    pieChart.setOption(pieOption);

    const handleResize = () => {
      pieChart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      pieChart.dispose();
      window.removeEventListener("resize", handleResize);
    };
  }, [totalSuccessCount, totalFailCount]);

  const containerStyle = useMemo(
    () => ({
      flexGrow: 1,
      height: "100%",
      margin: spacingToPx(2),
      paddingBottom: "80px",
    }),
    []
  );

  const statsContainerStyle = useMemo(
    () => ({
      display: "flex",
      flexWrap: "wrap",
      gap: spacingToPx(1.5),
      marginBottom: spacingToPx(2),
      justifyContent: "center",
    }),
    []
  );

  const statCardStyle = useMemo(
    () => ({
      borderRadius: "16px",
      padding: spacingToPx(1.5),
      backgroundColor: "hsl(var(--card))",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
      flex: "1 1 calc(33.333% - 16px)",
      maxWidth: "300px",
      minWidth: "150px",
      textAlign: "center",
      "@media (max-width: 600px)": {
        flex: "1 1 calc(50% - 16px)",
      },
    }),
    []
  );

  return (
    <div style={containerStyle}>
      <div style={statsContainerStyle}>
        <div style={statCardStyle}>
          <div style={{ fontSize: "16px", fontWeight: "bold" }}>
            Total Runned Count :
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#2196f3",
            }}
          >
            {totalRunCount}
          </div>
        </div>
        <div style={statCardStyle}>
          <div style={{ fontSize: "16px", fontWeight: "bold" }}>
            Total Success Count :
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#4caf50",
            }}
          >
            {totalSuccessCount}
          </div>
        </div>
        <div style={statCardStyle}>
          <div style={{ fontSize: "16px", fontWeight: "bold" }}>
            Total Fail Count :
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#ff9800",
            }}
          >
            {totalFailCount}
          </div>
        </div>
      </div>
      <div
        style={{
          width: "100%",
          height: isSmallScreen ? "300px" : "400px",
        }}
      >
        <div id="history-chart" style={{ width: "100%", height: "100%" }} />
      </div>
      <div
        style={{
          width: "100%",
          height: isSmallScreen ? "300px" : "400px",
          marginTop: spacingToPx(2),
        }}
      >
        <div id="pie-chart" style={{ width: "100%", height: "100%" }} />
      </div>
    </div>
  );
};

export default AutomationHistoryStatistics;
